import { cloneDeep, isEmpty } from 'lodash'

export const MAX_WEBHOOKS_PER_TEAM = 10

// States
export const STATES = {
  DEACTIVATED: 'DEACTIVATED',
  ACTIVE: 'ACTIVE'
}

export const isActive = (webhook = {}) => webhook.state === STATES.ACTIVE

// default values used to fill in template
export const PAYLOAD_TOKEN_PLACEHOLDERS = {
  TEAM_ID: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
  ATTACK_ID: 'yyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy',
  ATTACK_TYPE: 'CPU',
  STATUS: 'FINISHED',
  STAGE: 'UserHalted',
  SOURCE: 'WebApp',
  TIME: '2020-07-04T21:00:00.000 +0000'
}

// this is intentionally not using template string
// because the format we use for tokens is the same as the template string format in JS
export const PAYLOAD_TEMPLATE =
  '{\n\
  "teamId": "${TEAM_ID}",\n\
  "attackId": "${ATTACK_ID}",\n\
  "attackType": "${ATTACK_TYPE}",\n\
  "attackStatus": "${STATUS}",\n\
  "finalStage": "${STAGE}",\n\
  "source": "${SOURCE}",\n\
  "time": "${TIME}"\n\
}'

export function applyTemplateTokens(template, tokens) {
  let result = String(template)
  Object.keys(tokens).forEach(
    (token) => (result = result.replace(`\${${token}}`, tokens[token]))
  )
  return result
}

export function generatePayload(payload) {
  return applyTemplateTokens(payload, PAYLOAD_TOKEN_PLACEHOLDERS)
}

export const WEBHOOK_DEFAULT_VALUES = {
  uri: '',
  name: '',
  description: '',
  headers: [['', '']],
  method: 'POST',
  format: 'JSON',
  eventInitializing: false,
  eventRunning: false,
  eventFinished: false,
  state: STATES.ACTIVE,
  payload: PAYLOAD_TEMPLATE
}

// NOTE: GET is not supported because we can't reliably pass along the payload
export const WEBHOOK_METHODS = [
  { label: 'POST', value: 'POST' },
  { label: 'PUT', value: 'PUT' }
]

export const WEBHOOK_PROTOCOLS = ['http', 'https']

export const WEBHOOK_TRIGGER_EVENTS = {
  eventInitializing: {
    value: 'INITIALIZING',
    label: 'Attack Initializing',
    subtext: 'Any time an attack enters the initializing stage'
  },
  eventRunning: {
    value: 'RUNNING',
    label: 'Attack Running',
    subtext: 'Any time an attack enters the running stage'
  },
  eventFinished: {
    value: 'FINISHED',
    label: 'Attack Finished',
    subtext: 'Any time an attack enters the finished stage'
  }
}

export const WEBHOOK_FORMATS = [{ label: 'JSON', value: 'JSON' }]

export function convertWebhookForSaving(webhook) {
  // weird. tried using lodash's cloneDeep here, but it didn't clone `headers` properly
  // maybe it's because we're switching headers from an array of arrays to an object?
  // also, only copy these specific fields.
  // if we loaded an existing one and we edit it, we don't want to send all the fields back
  const copy = {
    description: webhook.description,
    isPrivateNetwork: webhook.isPrivateNetwork,
    method: webhook.method,
    name: webhook.name,
    uri: webhook.uri,
    triggerEvents: [],
    state: webhook.state,
    headers: {},
    payload: webhook.payload
  }

  // convert individual event fields back into array
  Object.keys(WEBHOOK_TRIGGER_EVENTS).forEach((eventKey) => {
    if (webhook[eventKey]) {
      copy.triggerEvents.push(WEBHOOK_TRIGGER_EVENTS[eventKey].value)
    }
    delete copy[eventKey]
  })

  // backend expects object of header name -> value properties
  if (webhook.headers?.length > 0) {
    copy.headers = Object.fromEntries(
      webhook.headers.filter(([key]) => Boolean(key))
    )
  }

  // do not pass an empty description to the endpoint
  const description = copy.description ?? ''
  if (description.trim().length < 1) {
    delete copy.description
  }

  // format is locked for now
  delete copy.format

  return copy
}

export function convertWebhookForDisplay(webhook) {
  const copy = cloneDeep(webhook)

  // unpack endpoint data
  if (copy.webhookEndpoint) {
    copy.uri = copy.webhookEndpoint.uri
    copy.headers = copy.webhookEndpoint.headers
    copy.method = copy.webhookEndpoint.method
    delete copy.webhookEndpoint
  }

  // if there aren't any headers saved then set the initial data so the inputs display
  if (isEmpty(copy.headers)) {
    copy.headers = WEBHOOK_DEFAULT_VALUES.headers
  } else if (!Array.isArray(copy.headers)) {
    copy.headers = Object.keys(copy.headers).map((key) => [
      key,
      copy.headers[key]
    ])
  }

  // this is not adjustable yet
  copy.format = WEBHOOK_DEFAULT_VALUES.format

  // convert event list to individual fields
  Object.keys(WEBHOOK_TRIGGER_EVENTS).forEach((eventKey) => {
    copy[eventKey] =
      webhook.triggerEvents?.includes(WEBHOOK_TRIGGER_EVENTS[eventKey].value) ??
      false
  })

  return copy
}
